import React, { useState, FormEvent } from "react";
import { API_URLS } from "../../config/apiUrls";
import {
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Box,
  Typography,
  Collapse, // Importação adicionada
  Switch,
  FormControlLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add"; // Ícone para adicionar

interface PlanosAdicionarProps {
  token: string;
  onPlanAdded: () => void; // Função que será chamada após a adição do plano
}

const PlanosAdicionar: React.FC<PlanosAdicionarProps> = ({
  token,
  onPlanAdded,
}) => {
  // NOVO ESTADO para controlar a visibilidade do formulário
  const [showForm, setShowForm] = useState(false);

  // Estados do formulário
  const [nomePlano, setNomePlano] = useState<string>("");
  const [descricaoPlano, setDescricaoPlano] = useState<string>("");
  const [valorPlano, setValorPlano] = useState<string>("");
  const [duracaoPlano, setDuracaoPlano] = useState<number>(30); // Duração padrão
  const [planoAtivo, setPlanoAtivo] = useState<boolean>(true);
  const [inicioPromocao, setInicioPromocao] = useState<string>("");
  const [fimPromocao, setFimPromocao] = useState<string>("");
  const [limitePorUsuario, setLimitePorUsuario] = useState<number>(2);
  const [error, setError] = useState<string | null>(null);
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  // Função para exibir a mensagem do Snackbar
  const showSnackbar = (message: string, severity: "success" | "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  // Função para limpar os campos do formulário
  const resetFormFields = () => {
    setNomePlano("");
    setDescricaoPlano("");
    setValorPlano("");
    setDuracaoPlano(30); // Resetando para valor padrão
    setPlanoAtivo(true);
    setInicioPromocao("");
    setFimPromocao("");
    setLimitePorUsuario(2);
    setError(null);
  };

  // Função para adicionar o plano
  const adicionarPlano = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Limpa erros anteriores
    setError(null);

    // Validação dos campos obrigatórios
    if (!nomePlano || valorPlano === "" || !duracaoPlano) {
      const msg = "Nome, Valor e Duração são obrigatórios.";
      setError(msg);
      showSnackbar(msg, "error");
      return;
    }

    const valorNumerico = parseFloat(valorPlano);
    if (valorNumerico < 0 || (valorNumerico === 0 && limitePorUsuario < 1)) {
      const msg = "Informe um valor válido e um limite maior que zero.";
      setError(msg);
      showSnackbar(msg, "error");
      return;
    }

    if (
      inicioPromocao &&
      fimPromocao &&
      new Date(fimPromocao) <= new Date(inicioPromocao)
    ) {
      const msg = "A data final deve ser posterior à data inicial.";
      setError(msg);
      showSnackbar(msg, "error");
      return;
    }

    const body = {
      name: nomePlano,
      description: descricaoPlano || "", // Descrição é opcional
      price: valorNumerico,
      duration: duracaoPlano,
      isActive: planoAtivo,
      startsAt: inicioPromocao
        ? new Date(inicioPromocao).toISOString()
        : null,
      endsAt: fimPromocao ? new Date(fimPromocao).toISOString() : null,
      limitPerUser: valorNumerico === 0 ? limitePorUsuario : null,
    };

    try {
      const response = await fetch(API_URLS.planos, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        showSnackbar("Plano adicionado com sucesso!", "success");
        resetFormFields(); // Limpa os campos
        setShowForm(false); // Esconde o formulário
        onPlanAdded(); // Chama a função de atualização após adicionar o plano
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.message || "Erro ao adicionar o plano.";
        setError(errorMessage);
        showSnackbar(errorMessage, "error");
      }
    } catch (err) {
      const errorMessage = "Erro ao conectar com o servidor.";
      setError(errorMessage);
      showSnackbar(errorMessage, "error");
    }
  };

  return (
    <Box sx={{ padding: 3 }}>
      {/* BOTÃO PARA MOSTRAR/OCULTAR O FORMULÁRIO */}
      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          color={showForm ? "secondary" : "primary"}
          startIcon={<AddIcon />}
          onClick={() => {
            setShowForm(!showForm);
            if (showForm) {
              resetFormFields(); // Limpa os campos se o formulário for fechado
            }
          }}
        >
          {showForm ? "Ocultar Formulário" : "Adicionar Novo Plano"}
        </Button>
      </Box>

      {/* COLLAPSE que envolve o formulário */}
      <Collapse in={showForm}>
        <Box
          sx={{
            padding: 2,
            border: "1px solid #ddd",
            borderRadius: "4px",
            mb: 2, // Espaçamento após o formulário
          }}
        >
          <Typography variant="h6" gutterBottom>
            Detalhes do Novo Plano
          </Typography>
          <form onSubmit={adicionarPlano}>
            {/* Nome do Plano */}
            <TextField
              label="Nome do Plano"
              value={nomePlano}
              onChange={(e) => setNomePlano(e.target.value)}
              required
              fullWidth
              margin="normal"
              // Ajusta a exibição do erro, se houver
              error={!!error && !nomePlano}
            />

            {/* Descrição do Plano */}
            <TextField
              label="Descrição (opcional)"
              value={descricaoPlano}
              onChange={(e) => setDescricaoPlano(e.target.value)}
              fullWidth
              margin="normal"
              multiline
              rows={2}
            />

            {/* Valor do Plano */}
            <TextField
              label="Valor do Plano"
              type="number"
              value={valorPlano}
              onChange={(e) => setValorPlano(e.target.value)}
              required
              fullWidth
              margin="normal"
              error={!!error && !valorPlano}
              inputProps={{ min: 0, step: "0.01" }}
            />

            {Number(valorPlano) === 0 && valorPlano !== "" && (
              <TextField
                label="Limite por usuário"
                type="number"
                value={limitePorUsuario}
                onChange={(e) => setLimitePorUsuario(Number(e.target.value))}
                required
                fullWidth
                margin="normal"
                inputProps={{ min: 1, step: 1 }}
              />
            )}

            {/* Duração do Plano */}
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Duração (dias)</InputLabel>
              <Select
                value={duracaoPlano}
                onChange={(e) => setDuracaoPlano(Number(e.target.value))}
                label="Duração (dias)"
              >
                <MenuItem value={7}>7 dias</MenuItem>
                <MenuItem value={30}>30 dias</MenuItem>
                <MenuItem value={60}>60 dias</MenuItem>
                <MenuItem value={90}>90 dias</MenuItem>
                <MenuItem value={180}>180 dias</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Início da disponibilidade (opcional)"
              type="datetime-local"
              value={inicioPromocao}
              onChange={(e) => setInicioPromocao(e.target.value)}
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Fim da disponibilidade (opcional)"
              type="datetime-local"
              value={fimPromocao}
              onChange={(e) => setFimPromocao(e.target.value)}
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={planoAtivo}
                  onChange={(e) => setPlanoAtivo(e.target.checked)}
                />
              }
              label="Plano habilitado"
            />

            {/* Botão para adicionar o plano */}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 2 }}
              disabled={!nomePlano || valorPlano === "" || !duracaoPlano} // Desabilita se campos obrigatórios estiverem vazios
            >
              Adicionar Plano
            </Button>
          </form>
        </Box>
      </Collapse>

      {/* Snackbar para mostrar mensagens de sucesso ou erro */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000} // Duração que a mensagem ficará visível
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PlanosAdicionar;
